import type { MissionAction, WeeklyMissionPayload } from '../api';
import { toLocalDateInputValue } from '../dateUtils';
import { missionAction, missionProgressLabel, missionTaskTitle, sortMissionTasks } from './missionModel';

export type MealHistoryItem = {
  id: number; date: string; created_at?: string; meal_type: string;
  food_name: string; calories: number; note?: string;
};
export type NoteHistoryItem = { id: number; date: string; created_at?: string; content: string };
export type RecordFilter = 'all' | 'meal' | 'note';
export type HomeRecord =
  | { kind: 'meal'; key: string; item: MealHistoryItem }
  | { kind: 'note'; key: string; item: NoteHistoryItem };
export type HomeAction = {
  key: string; title: string; subtitle: string; dimension: string;
  action: MissionAction; progressLabel?: string; priority: number;
};

export function getHomeActions(mission: WeeklyMissionPayload | null): HomeAction[] {
  if (!mission) return [];
  const study: HomeAction[] = (mission.daily_study || []).filter(item => item.status !== 'completed').map(item => ({
    key: `study:${item.pathway_id}:${item.task_id}`, title: item.title,
    subtitle: '今日途径学习', dimension: 'intellect',
    action: { type: 'study', label: '去学习', pathwayId: item.pathway_id }, priority: 0,
  }));
  const names = new Map((mission.groups || []).map(group => [group.key, group.name]));
  const weekly: HomeAction[] = sortMissionTasks((mission.groups || []).flatMap(group =>
    group.tasks.map(task => ({ ...task, dimension: task.dimension || group.key }))))
    .filter(task => task.status !== 'completed' && !task.archived).map(task => ({
      key: `weekly:${task.id}`, title: missionTaskTitle(task),
      subtitle: `本周待办 · ${names.get(task.dimension) || task.dimension}`,
      dimension: task.dimension, action: missionAction(task), priority: task.priority ?? 0,
      progressLabel: task.verification === 'auto' ? missionProgressLabel(task) : undefined,
    }));
  // Surface different kinds of action instead of allowing several study items to hide all weekly goals.
  if (study.length && weekly.length) {
    const otherType = weekly.find(item => item.action.type !== 'study');
    return [study[0], otherType || weekly[0]];
  }
  const candidates = weekly.length ? weekly : study;
  if (!candidates.length) return [];
  const first = candidates[0];
  const second = candidates.find(item => item.action.type !== first.action.type)
    || candidates.find(item => item.dimension !== first.dimension) || candidates[1];
  return second ? [first, second] : [first];
}

/** Meals have a calendar date; note timestamps first need the user's local day. */
export function recordDay(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : toLocalDateInputValue(date);
}
const timestamp = (value?: string) => value ? new Date(value).getTime() || 0 : 0;

export function getRecentRecords(meals: MealHistoryItem[], notes: NoteHistoryItem[], filter: RecordFilter, limit = 2): HomeRecord[] {
  const rows: HomeRecord[] = [
    ...(filter === 'note' ? [] : meals.map(item => ({ kind: 'meal' as const, key: `meal:${item.id}`, item }))),
    ...(filter === 'meal' ? [] : notes.map(item => ({ kind: 'note' as const, key: `note:${item.id}`, item }))),
  ];
  return rows.sort((a, b) => {
    const dayOrder = recordDay(b.item.date).localeCompare(recordDay(a.item.date));
    // A backfilled older meal must not jump ahead of today's records.
    return dayOrder || timestamp(b.item.created_at || b.item.date) - timestamp(a.item.created_at || a.item.date)
      || b.item.id - a.item.id || a.key.localeCompare(b.key);
  }).slice(0, limit);
}
